// src/pages/dataInput/sources/FileSource.tsx
import React, { useEffect, useRef, useState } from "react";
import { cls } from "../../theme/theme";
import {
  classifyFile,
  checkFileAcceptance,
  generateValidationReport,
  generateCombinedValidationReport,
} from "../utils/sheetValidation";
import {
  FileClassification,
  SourcePayload,
  UploadedFile,
  SheetConfig,
  SheetMode,
  SheetWarning,
  Tool,
} from "../types";

interface Props {
  tool: Tool;
  initialFiles?:        UploadedFile[];
  initialSheetConfigs?: SheetConfig[];
  initialSheetMode?:    SheetMode;
  onChange: (payload: SourcePayload | null) => void;
}

export const FileSource: React.FC<Props> = ({
  tool,
  initialFiles        = [],
  initialSheetConfigs = [],
  initialSheetMode    = "sub_entities",
  onChange,
}) => {
  const [files,           setFiles]           = useState<UploadedFile[]>(initialFiles);
  const [classifications, setClassifications] = useState<FileClassification[]>([]);
  const [sheetConfigs,    setSheetConfigs]    = useState<SheetConfig[]>(initialSheetConfigs);
  const [sheetMode,       setSheetMode]       = useState<SheetMode>(initialSheetMode);
  const [analyzing,       setAnalyzing]       = useState(false);

  // Track auto-filled fields so user edits aren't overwritten
  const autoFilledRef = useRef<Set<string>>(new Set());

  // Index sheet banner state
  const [indexBanner, setIndexBanner] = useState<{
    fileName: string;
    matched:  number;
    total:    number;
  } | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Re-classify whenever files change ────────────────────────────────────
  useEffect(() => {
    if (files.length === 0) {
      setClassifications([]);
      setSheetConfigs([]);
      setIndexBanner(null);
      autoFilledRef.current.clear();
      onChange(null);
      return;
    }

    let cancelled = false;
    (async () => {
      setAnalyzing(true);
      try {
        const results = await Promise.all(files.map(f => classifyFile(f.file)));
        if (cancelled) return;
        setClassifications(results);

        const next: SheetConfig[] = [];
        let bannerFile    = "";
        let bannerMatched = 0;
        let bannerTotal   = 0;

        for (let fi = 0; fi < results.length; fi++) {
          const fc     = results[fi];
          const accept = checkFileAcceptance(fc, tool);
          const indexMap = fc.indexEntries;

          if (indexMap && indexMap.size > 0) {
            bannerFile  = fc.fileName;
            bannerTotal = fc.sheets.length;
          }

          if (fc.fileType === "csv" || fc.fileType === "excel") {
            fc.sheets.forEach((sheet, si) => {
              const autoKey = `${fc.fileName}::${sheet.sheetName}`;
              const existing = sheetConfigs.find(
                sc => sc.fileName === fc.fileName && sc.sheetName === sheet.sheetName
              );
              const accepted = accept.perSheet[si]?.accepted ?? false;
              const reason   = accept.perSheet[si]?.reason   ?? sheet.reason;

              let autoDisplayName = sheet.sheetName;
              let autoDescription = "";

              if (indexMap) {
                const entry = indexMap.get(sheet.sheetName)
                  ?? [...indexMap.values()].find(
                       e => e.sheetName.toLowerCase() === sheet.sheetName.toLowerCase()
                     );
                if (entry) {
                  bannerMatched++;
                  if (entry.fullName)    autoDisplayName = entry.fullName;
                  if (entry.description) autoDescription = entry.description;
                }
              }

              const freshLoad = !existing;

              const finalDisplayName = (() => {
                if (freshLoad) {
                  if (autoDisplayName !== sheet.sheetName) autoFilledRef.current.add(autoKey + "::display");
                  return autoDisplayName;
                }
                if (autoFilledRef.current.has(autoKey + "::display")) return autoDisplayName;
                return existing!.displayName;
              })();

              const finalDescription = (() => {
                if (freshLoad) {
                  if (autoDescription) autoFilledRef.current.add(autoKey + "::desc");
                  return autoDescription;
                }
                if (autoFilledRef.current.has(autoKey + "::desc")) return autoDescription;
                return existing!.description;
              })();

              next.push({
                fileName:         fc.fileName,
                sheetName:        sheet.sheetName,
                displayName:      finalDisplayName,
                description:      finalDescription,
                included:         existing ? existing.included && accepted : accepted,
                rowCount:         sheet.rowCount,
                colCount:         sheet.colCount,
                mode:             sheet.mode,
                accepted,
                acceptanceReason: reason,
                warnings:         sheet.warnings,
                processableRows:  sheet.processableRows,
                uniqueFields:     sheet.uniqueFields,
              });
            });
          }
        }

        setSheetConfigs(next);

        if (bannerFile && bannerMatched > 0) {
          setIndexBanner({ fileName: bannerFile, matched: bannerMatched, total: bannerTotal });
        } else {
          setIndexBanner(null);
        }
      } finally {
        if (!cancelled) setAnalyzing(false);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files, tool]);

  // ── Emit normalised payload upward ───────────────────────────────────────
  useEffect(() => {
    if (files.length === 0) { onChange(null); return; }
    const includedSheets = sheetConfigs.filter(s => s.included);
    if (includedSheets.length === 0 && classifications.some(c => c.sheets.length > 0)) {
      onChange(null);
      return;
    }

    const availableTables = includedSheets.length > 0
      ? includedSheets.map(s => ({
          prefix:  (s.displayName || s.sheetName).toUpperCase().replace(/\s+/g, "_"),
          label:   s.displayName || s.sheetName,
          columns: classifications
            .find(c => c.fileName === s.fileName)
            ?.sheets.find(sh => sh.sheetName === s.sheetName)
            ?.headers ?? [],
        }))
      : files.map(f => ({
          prefix:  f.name.replace(/\.[^.]+$/, "").toUpperCase().replace(/\s+/g, "_"),
          label:   f.name,
          columns: classifications.find(c => c.fileName === f.name)?.sheets[0]?.headers ?? [],
        }));

    const payload: SourcePayload = {
      kind:          "file",
      label:         files.length === 1 ? files[0].name : `${files.length} files`,
      sheetMode,
      files,
      sheetConfigs,
      availableTables,
      relationships: [],
    };
    onChange(payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files, sheetConfigs, sheetMode, classifications]);

  // ── Mutators ─────────────────────────────────────────────────────────────
  const addFiles = (fl: FileList | null) => {
    if (!fl) return;
    const next = [...files];
    Array.from(fl).forEach(f => {
      if (!next.find(x => x.name === f.name)) next.push({ name: f.name, file: f });
    });
    setFiles(next);
  };

  const removeFile = (name: string) => {
    setFiles(files.filter(f => f.name !== name));
    autoFilledRef.current = new Set(
      [...autoFilledRef.current].filter(k => !k.startsWith(name + "::"))
    );
  };

  const updateSheetConfig = (idx: number, patch: Partial<SheetConfig>) => {
    const sc  = sheetConfigs[idx];
    const key = `${sc.fileName}::${sc.sheetName}`;
    if ("displayName" in patch) autoFilledRef.current.delete(key + "::display");
    if ("description" in patch) autoFilledRef.current.delete(key + "::desc");
    setSheetConfigs(prev => prev.map((s, i) => i === idx ? { ...s, ...patch } : s));
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const hasExcelOrCsv = classifications.some(c => c.fileType === "csv" || c.fileType === "excel");
  const acceptedCount = sheetConfigs.filter(s => s.accepted).length;
  const includedCount = sheetConfigs.filter(s => s.included).length;

  return (
    <div>
      {/* Drop zone */}
      <label
        className={cls("gw-dropzone", files.length > 0 && "has-files")}
        onDragOver={e => e.preventDefault()}
        onDrop={e => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".csv,.xls,.xlsx,.xlsm"
          style={{ display: "none" }}
          onChange={e => addFiles(e.target.files)}
        />
        {files.length === 0 ? (
          <>
            <div className="gw-dropzone-icon">📂</div>
            <div className="gw-dropzone-label">
              Drag &amp; drop CSV or Excel files, or click to browse
            </div>
            <div className="gw-dropzone-sub">
              .csv · .xlsx · .xlsm — multiple files supported
            </div>
          </>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {files.map(f => (
              <span key={f.name} className="gw-badge gw-badge-brand" style={{ paddingRight: 4, fontSize: 11 }}>
                {f.name}
                <span
                  style={{ marginLeft: 5, cursor: "pointer", padding: "0 4px" }}
                  onClick={e => { e.preventDefault(); e.stopPropagation(); removeFile(f.name); }}
                >✕</span>
              </span>
            ))}
            <span
              className="gw-badge gw-badge-muted"
              style={{ cursor: "pointer", fontSize: 11 }}
              onClick={() => fileInputRef.current?.click()}
            >+ add more</span>
          </div>
        )}
      </label>

      {analyzing && (
        <div style={{ marginTop: 10, fontSize: 12, color: "#445566" }}>
          Inspecting file structure…
        </div>
      )}

      {/* Index sheet banner */}
      {indexBanner && (
        <div style={{
          display: "flex", alignItems: "flex-start", justifyContent: "space-between",
          gap: 12, marginTop: 12, padding: "10px 14px", borderRadius: 8,
          background: "#f0fdf4", border: "1px solid #86efac",
        }}>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            <span style={{ fontSize: 16, lineHeight: 1.4, flexShrink: 0 }}>📋</span>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#166534" }}>
                Index sheet detected — {indexBanner.matched} of {indexBanner.total} sheets matched
              </div>
              <div style={{ fontSize: 11, color: "#16a34a", marginTop: 2 }}>
                Display names and entity descriptions have been auto-filled from the Index.
                Edit any field below to override.
              </div>
            </div>
          </div>
          <button
            onClick={() => setIndexBanner(null)}
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: "#166534", fontSize: 14, lineHeight: 1, padding: "2px 4px",
              flexShrink: 0,
            }}
          >✕</button>
        </div>
      )}

      {/* Per-sheet panel */}
      {hasExcelOrCsv && sheetConfigs.length > 0 && (
        <div className="gw-validation-panel" style={{ marginTop: 14 }}>
          <div className="gw-validation-hdr">
            <span>
              {sheetConfigs.length} sheet{sheetConfigs.length === 1 ? "" : "s"} detected
              {" · "}
              <span style={{ color: "#166534", fontWeight: 700 }}>
                {acceptedCount} valid for {tool.replace("BUILD_", "").replace("_USING_DATA", "")}
              </span>
              {" · "}
              <span style={{ color: "#1e3a6e", fontWeight: 700 }}>{includedCount} selected</span>
            </span>
          </div>

          {/* Mode toggle */}
          {includedCount > 1 && (
            <div style={{ padding: "12px 14px 0" }}>
              <div className="gw-mode-toggle">
                {(["sub_entities", "separate_entities"] as SheetMode[]).map(m => (
                  <button
                    key={m}
                    className={cls("gw-mode-toggle-btn", sheetMode === m && "active")}
                    onClick={() => setSheetMode(m)}
                  >
                    {m === "sub_entities" ? "Group as one entity" : "One entity per sheet"}
                  </button>
                ))}
              </div>
              <div className="gw-mode-explainer">
                {sheetMode === "sub_entities"
                  ? "All selected sheets will be grouped as sub-entities under a single parent entity."
                  : "Each selected sheet becomes its own independent entity."}
              </div>
            </div>
          )}

          {/* ── Validation summary (aggregated across included sheets) ──── */}
          {(() => {
            const included = sheetConfigs.filter(s => s.included);
            if (included.length === 0) return null;

            const totalRows       = included.reduce((s, sc) => s + (sc.rowCount ?? 0), 0);
            const totalLoaded     = included.reduce((s, sc) => s + (sc.processableRows ?? sc.rowCount ?? 0), 0);
            const totalUnique     = included.reduce((s, sc) => s + (sc.uniqueFields ?? sc.processableRows ?? sc.rowCount ?? 0), 0);
            const totalDropped    = totalRows - totalLoaded;
            const hasAnyWarnings  = included.some(sc => sc.warnings && sc.warnings.length > 0);

            if (included.length < 2 && !hasAnyWarnings) return null;

            return (
              <div style={{
                margin: "10px 14px 4px", padding: "10px 14px", borderRadius: 8,
                background: totalDropped > 0 ? "#fffbeb" : "#f0fdf4",
                border: `1px solid ${totalDropped > 0 ? "#fcd34d" : "#86efac"}`,
                display: "flex", flexWrap: "wrap", alignItems: "center",
                gap: "6px 16px", fontSize: 11,
              }}>
                <span style={{ fontWeight: 700, color: "#1e3a6e" }}>
                  📊 {included.length} sheet{included.length > 1 ? "s" : ""}
                </span>
                <span style={{ color: "#445566" }}>
                  {totalRows.toLocaleString()} total rows
                </span>
                <span style={{ fontWeight: 700, color: "#166534" }}>
                  → {totalLoaded.toLocaleString()} fields will be loaded
                </span>
                {totalUnique < totalLoaded && (
                  <span style={{ color: "#854d0e" }}>
                    ({totalUnique.toLocaleString()} unique)
                  </span>
                )}
                {totalDropped > 0 && (
                  <span style={{ color: "#854d0e" }}>
                    ⚠️ {totalDropped.toLocaleString()} dropped
                  </span>
                )}
                {included.length > 1 && (
                  <button
                    onClick={() => {
                      const f = files.find(fi => fi.name === included[0]?.fileName);
                      if (f) generateCombinedValidationReport(
                        f.file,
                        included.map(sc => sc.sheetName),
                      );
                    }}
                    style={{
                      marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 4,
                      fontSize: 10, fontWeight: 600, padding: "3px 10px", borderRadius: 5,
                      cursor: "pointer", border: "1px solid #c8d4e0",
                      background: "#fff", color: "#445566",
                    }}
                  >
                    📥 Download all validation
                  </button>
                )}
              </div>
            );
          })()}

          {/* Per-sheet rows */}
          {sheetConfigs.map((sc, idx) => {
            const disabled       = !sc.accepted;
            const autoKey        = `${sc.fileName}::${sc.sheetName}`;
            const isDescAutofill = autoFilledRef.current.has(autoKey + "::desc") && !!sc.description;
            const hasWarnings    = sc.warnings && sc.warnings.length > 0;

            return (
              <div
                key={`${sc.fileName}::${sc.sheetName}`}
                className={cls("gw-sheet-row", sc.included && "included", disabled && "disabled")}
              >
                <input
                  type="checkbox"
                  className="gw-sheet-checkbox"
                  checked={sc.included}
                  disabled={disabled}
                  onChange={e => updateSheetConfig(idx, { included: e.target.checked })}
                />
                <div className="gw-sheet-info">
                  <div className="gw-sheet-name-row">
                    <span className="gw-sheet-name">{sc.sheetName}</span>
                    {classifications.length > 1 && (
                      <span className="gw-sheet-meta">in {sc.fileName}</span>
                    )}
                    <span className={cls("gw-mode-badge", sc.mode)}>{sc.mode}</span>
                    {disabled && <span className="gw-mode-badge rejected">rejected</span>}
                    {sc.colCount !== undefined && (
                      <span className="gw-sheet-meta">
                        {sc.colCount} col{sc.colCount === 1 ? "" : "s"}
                        {sc.rowCount ? ` · ${sc.rowCount.toLocaleString()} rows` : ""}
                      </span>
                    )}
                    {isDescAutofill && !disabled && (
                      <span style={{
                        fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 4,
                        background: "#f0fdf4", color: "#166534", border: "1px solid #86efac",
                        flexShrink: 0,
                      }}>
                        📋 from index
                      </span>
                    )}
                  </div>
                  <div className="gw-sheet-reason">{sc.acceptanceReason}</div>

                  {/* Inline data quality warnings */}
                  {hasWarnings && (
                    <div style={{ marginTop: 6 }}>
                      {sc.warnings!.map((w, wi) => (
                        <div key={wi} style={{
                          display: "flex", gap: 5, padding: "2px 0",
                          fontSize: 11, lineHeight: 1.5,
                          color: w.severity === "error" ? "#991b1b"
                               : w.severity === "warning" ? "#854d0e" : "#6b7280",
                        }}>
                          <span style={{ flexShrink: 0 }}>
                            {w.severity === "error" ? "⛔"
                             : w.severity === "warning" ? "⚠️" : "ℹ️"}
                          </span>
                          <div>
                            <span>{w.message}</span>
                            {w.samples && w.samples.length > 0 && (
                              <span style={{
                                marginLeft: 6, fontSize: 10,
                                fontFamily: "'JetBrains Mono', monospace",
                                color: "#9ca3af",
                              }}>
                                {w.samples.slice(0, 3).join(" · ")}
                                {w.samples.length > 3 && ` (+${w.samples.length - 3})`}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                      {/* Summary: fields that will be loaded */}
                      <div style={{
                        marginTop: 6, padding: "5px 10px", borderRadius: 6,
                        background: "#f0fdf4", border: "1px solid #d1fae5",
                        fontSize: 11, fontWeight: 600, color: "#166534",
                        display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap",
                      }}>
                        <span>
                          ✅ {(sc.processableRows ?? sc.rowCount ?? 0).toLocaleString()} fields will be loaded
                        </span>
                        {sc.processableRows != null && sc.processableRows < (sc.rowCount ?? 0) && (
                          <span style={{ fontWeight: 400, color: "#6b7280", fontSize: 10 }}>
                            (out of {(sc.rowCount ?? 0).toLocaleString()} total rows)
                          </span>
                        )}
                        {sc.uniqueFields != null && sc.uniqueFields < (sc.processableRows ?? 0) && (
                          <span style={{ fontWeight: 400, color: "#854d0e", fontSize: 10 }}>
                            · {sc.uniqueFields.toLocaleString()} unique — duplicates get suffix
                          </span>
                        )}
                      </div>
                      {/* Download validation report */}
                      <button
                        onClick={() => {
                          const f = files.find(fi => fi.name === sc.fileName);
                          if (f) generateValidationReport(f.file, sc.sheetName);
                        }}
                        style={{
                          marginTop: 6, display: "inline-flex", alignItems: "center", gap: 4,
                          fontSize: 10, fontWeight: 600, padding: "3px 10px", borderRadius: 5,
                          cursor: "pointer", border: "1px solid #c8d4e0",
                          background: "#f8fafc", color: "#445566",
                        }}
                      >
                        📥 Download validation report
                      </button>
                    </div>
                  )}

                  {sc.included && !disabled && (
                    <div className="gw-sheet-fields">
                      <div className="gw-sheet-fields-grid">
                        <div className="gw-sheet-field-row">
                          <input
                            className="gw-sheet-field-edit"
                            placeholder="Display name"
                            value={sc.displayName}
                            onChange={e => updateSheetConfig(idx, { displayName: e.target.value })}
                          />
                        </div>
                        <div className="gw-sheet-field-row">
                          <textarea
                            className="gw-sheet-field-edit"
                            placeholder="Entity description (auto-filled from Index sheet if present)"
                            value={sc.description}
                            rows={sc.description && sc.description.length > 80 ? 3 : 1}
                            style={{ resize: "vertical", lineHeight: 1.5 }}
                            onChange={e => updateSheetConfig(idx, { description: e.target.value })}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FileSource;